/**
 * Lab administration.
 *
 * The shell, and only the shell: a persistent left nav over the four things that can be authored, a
 * top bar that searches all of them and creates any of them, and a breadcrumb that says where in the
 * tree the builder currently is. Everything that edits anything lives in `LabBuilder` or in one of
 * the four library views.
 *
 * Four sections rather than nine tabs, because the old tabs were not four different objects - they
 * were one object at four depths. "Devices & steps", "Validation" and "Checks" were all the selected
 * scenario, and which scenario that was lived in a select at the top of the page that no tab
 * mentioned. Depth is now shown as depth, in the builder's tree, and the nav lists only things that
 * exist independently of each other.
 *
 * The two reference tables are kept, under their own heading. A device type and an execution method
 * are neither library objects nor part of any one lab: they are the vocabulary everything above is
 * authored against, and dropping them from the nav would make the seventh platform unaddable.
 *
 * One search box, and it is the shell's rather than each list's. The term survives switching
 * sections, and the nav counts what it matches in the sections that are not showing - which is the
 * actual question behind most searching here, since an admin who cannot find a firewall in Devices
 * usually wants to know whether it is a device at all.
 *
 * No user menu or logo in this bar: this page renders inside the portal shell, which already has
 * both, and a second one would be a second set of controls that do almost the same thing.
 *
 * `LabToastHost` is mounted here, once. Every write in this tree reports itself through
 * `useLabToasts`, and until something rendered the host those reports went nowhere.
 */

import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  HbrBreadcrumb,
  HbrBreadcrumbItem,
  HbrButton,
  HbrDropdown,
  HbrIcon,
  HbrMenu,
  HbrMenuItem,
} from '@harbor/elements-react-standalone';
import {
  folder,
  gearSix,
  listBullets,
  network,
  plus,
  stack,
  stackSimple,
} from '@harbor/elements/icons';
import NavHistoryTracker from '../../../navigation/NavHistoryTracker';
import { useAdminPermissions } from '../Hooks/useAdminPermissions';
import { ADMIN_URL_LABS_GENERATION } from './admin-values';
import LabBuilder, { LabBuilderNode } from '../../Lab/LabBuilder';
import LabDeviceTypesPanel from '../../Lab/LabDeviceTypesPanel';
import LabExecutionMethodsPanel from '../../Lab/LabExecutionMethodsPanel';
import LabEditorModal from '../../Lab/LabEditorModal';
import LabScenarioEditorModal from '../../Lab/LabScenarioEditorModal';
import LabConfigurationItemEditorModal from '../../Lab/LabConfigurationItemEditorModal';
import LabDeviceEditorModal from '../../Lab/LabDeviceEditorModal';
import {
  ConfigurationItemsLibraryView,
  DevicesLibraryView,
  LabsLibraryView,
  ScenariosLibraryView,
  labMatchesDevice,
  labMatchesItem,
  labMatchesLab,
  labMatchesScenario,
} from '../../Lab/LabLibraryViews';
import { LabSearchBox, LabToastHost, useLabToasts } from '../../Lab/LabAdminShared';
import {
  useLabConfigurationItems,
  useLabDevices,
  useLabScenarioLinks,
  useLabScenarios,
  useLabs,
} from '../../../hooks/lab-hooks';
import {
  Lab,
  LabConfigurationItem,
  LabScenario,
  LabDevice,
} from '../../../support/labService';

type LabAdminSectionKey =
  | 'labs'
  | 'scenarios'
  | 'items'
  | 'devices'
  | 'deviceTypes'
  | 'methods';

type LabAdminSection = {
  key: LabAdminSectionKey;
  label: string;
  /** Read by `isVisibleTab`, which expects `{ key, label, pageView }`. */
  pageView: string;
  /** Icon asset from `@harbor/elements/icons`. Some are lazy getters, hence the union. */
  asset: string | (() => string);
  /** Under the label. What this list is, so the nav does not need to be learned by clicking. */
  blurb: string;
  /** The vocabulary tables, listed under their own heading rather than beside the objects. */
  reference?: boolean;
};

/**
 * The nav, in the order things are made in.
 *
 * A lab first because it is what gets delivered, then the sections it composes, then the items those
 * sections attach, then the devices those items land on. Read downwards it is also the containment
 * order, which is the same order the builder's tree draws.
 */
const LAB_ADMIN_SECTIONS: LabAdminSection[] = [
  {
    key: 'labs',
    label: 'Labs',
    pageView: 'page.labs.view',
    asset: stack,
    blurb: 'Composed of sections, published per data centre',
  },
  {
    key: 'scenarios',
    label: 'Scenarios',
    pageView: 'page.labs.view',
    asset: folder,
    blurb: 'The reusable sections - one can be in many labs',
  },
  {
    key: 'items',
    label: 'Configuration items',
    pageView: 'page.labs.view',
    asset: listBullets,
    blurb: 'The catalogue of work, shared by every section',
  },
  {
    key: 'devices',
    label: 'Devices',
    pageView: 'page.labs.view',
    asset: network,
    blurb: 'The shared catalogue of boxes, attachable to many sections',
  },
  {
    key: 'deviceTypes',
    label: 'Device types',
    pageView: 'page.labs.view',
    asset: stackSimple,
    blurb: 'What kind of box an item is written for',
    reference: true,
  },
  {
    key: 'methods',
    label: 'Execution methods',
    pageView: 'page.labs.view',
    asset: gearSix,
    blurb: 'How work is carried out - ssh, REST, Ansible',
    reference: true,
  },
];

const AdminLabGeneration = () => {
  const { section } = useParams();
  const navigate = useNavigate();
  const { isVisibleTab, isAdminFull } = useAdminPermissions();
  const toasts = useLabToasts();

  /**
   * Which lab the builder has open, and where in it.
   *
   * Held by the shell rather than the builder so it survives a trip to the library: "view in
   * library" on a row menu is a detour, and coming back to a collapsed tree at the top would make
   * the detour cost more than the answer is worth.
   */
  const [openLabId, setOpenLabId] = useState<number | null>(null);
  const [selected, setSelected] = useState<LabBuilderNode>({ kind: 'lab', label: '' });
  const [term, setTerm] = useState('');
  /**
   * The row a "view in library" jump was about.
   *
   * Highlighted rather than scrolled to, because these lists are filtered and a row that is not
   * currently shown cannot be scrolled to at all - the highlight is what makes the jump land on the
   * row instead of on the table. Cleared as soon as the nav is used, since by then it is somebody
   * else's row.
   */
  const [libraryFocus, setLibraryFocus] = useState<number | null>(null);

  const [labEditor, setLabEditor] = useState<{ open: boolean; lab: Lab | null }>({
    open: false,
    lab: null,
  });
  const [scenarioEditor, setScenarioEditor] = useState<{
    open: boolean;
    scenario: LabScenario | null;
  }>({ open: false, scenario: null });
  const [itemEditor, setItemEditor] = useState<{
    open: boolean;
    item: LabConfigurationItem | null;
  }>({ open: false, item: null });
  /**
   * Null closed. `scenario` null means "opened from the library, with no section pre-ticked".
   *
   * A device belongs to no scenario - it is a library entry, attached to as many sections as use it
   * - so the editor edits that set as a list. Opened from inside a section, that section starts
   * ticked; opened from the library, nothing does, and a box saved attached to nothing is a
   * catalogue entry rather than a mistake.
   */
  const [deviceEditor, setDeviceEditor] = useState<{
    scenario: LabScenario | null;
    device: LabDevice | null;
  } | null>(null);

  const visible = useMemo(
    () => {
      const allowed = LAB_ADMIN_SECTIONS.filter((entry) => isVisibleTab(entry));

      // `page.labs.view` is not seeded yet, so on most tenants only admin.full passes. Rather than
      // hide the whole page from admins who can already reach every other admin tab, fall back to
      // the full list when nothing matched - the API authorizes every write again regardless.
      return allowed.length ? allowed : LAB_ADMIN_SECTIONS;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isVisibleTab],
  );

  const current = useMemo(
    () => visible.find((entry) => entry.key === section) || visible[0],
    [section, visible],
  );

  const go = (key: LabAdminSectionKey) => navigate(`${ADMIN_URL_LABS_GENERATION}/${key}`);

  const canEdit = isAdminFull || isVisibleTab(LAB_ADMIN_SECTIONS[0]);

  /**
   * The four lists, here as well as in the views.
   *
   * Not a duplicate fetch - react-query serves both from one cache entry - and the shell needs them
   * for three things a view cannot answer: the match counts in the nav, the lab name in the
   * breadcrumb, and resolving a device to the section its editor has to save against.
   */
  const { data: labs } = useLabs();
  const { data: scenarios } = useLabScenarios();
  const { data: links } = useLabScenarioLinks();
  const { data: items } = useLabConfigurationItems();
  const { data: devices } = useLabDevices();

  const scenarioById = useMemo(
    () => new Map((scenarios || []).map((scenario) => [scenario.id, scenario])),
    [scenarios],
  );

  const openLab = useMemo(
    () => (labs || []).find((row) => row.id === openLabId) || null,
    [labs, openLabId],
  );

  /** The builder replaces the lab list, rather than sitting beside it. It is a full screen of tree. */
  const showBuilder = current.key === 'labs' && !!openLabId;

  const needle = term.trim().toLowerCase();

  /**
   * What the term matches in each of the four lists.
   *
   * Counted with the views' own predicates, exported for exactly this - a badge that disagrees with
   * the list it leads to would be worse than no badge, since the whole point of it is to say "stop
   * looking here".
   */
  const matches = useMemo(() => {
    if (!needle) return null;

    return {
      labs: (labs || []).filter((row) => labMatchesLab(row, needle)).length,
      scenarios: (scenarios || []).filter((row) => labMatchesScenario(row, needle)).length,
      items: (items || []).filter((row) => labMatchesItem(row, needle)).length,
      devices: (devices || []).filter((row) => labMatchesDevice(row, needle)).length,
    } as Record<string, number>;
  }, [needle, labs, scenarios, items, devices]);

  /* ----------------------------------------------------------------------- *
   * Opening things
   * ----------------------------------------------------------------------- */

  const openLabInBuilder = (labId: number) => {
    const row = (labs || []).find((entry) => entry.id === labId) || null;

    setOpenLabId(labId);
    setSelected({ kind: 'lab', label: row?.name || 'Lab' });

    if (current.key !== 'labs') go('labs');
  };

  /**
   * A scenario, opened where its containment is visible.
   *
   * The builder is rooted on a lab, so a section can only be opened in one - and a scenario in no
   * lab has no tree to appear in. That is a normal state for a freshly written section rather than
   * an error, so it is said plainly and the row menu's Edit still works.
   */
  const openScenarioInBuilder = (scenarioId: number) => {
    const scenario = scenarioById.get(scenarioId) || null;
    const candidates = (links || []).filter((entry) => entry.scenario === scenarioId);
    const link =
      candidates.find((entry) => entry.enabled && entry.lab_enabled !== false) || candidates[0];

    if (!link) {
      toasts.said(
        `${
          scenario?.name || 'That section'
        } is in no lab yet, so there is no tree to open it in. Attach it to a lab from the lab's builder, or edit it from its row menu.`,
      );

      return;
    }

    setOpenLabId(link.lab);
    setSelected({
      kind: 'scenario',
      scenarioId,
      linkId: link.id,
      label: scenario?.name || 'Section',
    });

    if (current.key !== 'labs') go('labs');
  };

  /**
   * A device's editor, opened without a section.
   *
   * There is none to pass: a device is a library entry attached to as many sections as use it, so
   * the editor asks which ones as a list rather than being told one. Null here is the honest
   * answer, and it is what makes the modal show the whole attachment set instead of assuming the
   * section somebody happened to arrive from.
   */
  const openDeviceEditor = (deviceId: number) => {
    const device = (devices || []).find((entry) => entry.id === deviceId) || null;

    if (!device) {
      toasts.failed('open that device', new Error('It is no longer in the list.'));

      return;
    }

    setDeviceEditor({ scenario: null, device });
  };

  /** Where "+ New device" would put one, and what the builder's own buttons are scoped by. */
  const contextScenario = useMemo(() => {
    if (!showBuilder) return null;

    if (selected.kind === 'scenario' || selected.kind === 'item' || selected.kind === 'device') {
      return scenarioById.get(selected.scenarioId) || null;
    }

    return null;
  }, [showBuilder, selected, scenarioById]);

  /* ----------------------------------------------------------------------- *
   * The bar
   * ----------------------------------------------------------------------- */

  /**
   * `Labs / <lab> / <section> / <item>`, from the selection itself.
   *
   * Each node carries its own label, so this needs no second read of the tree's data. The section
   * crumb for an item or a device is looked up, because those nodes carry the section's id and not
   * its name - the name is the shell's to find, and getting it from the scenario list is one map.
   */
  const crumbs = useMemo(() => {
    const trail: { key: string; label: string; onSelect?: () => void }[] = [
      {
        key: 'section',
        label: current.label,
        // "Up", not "go to this section": from inside a lab the thing above it is the lab list.
        onSelect: showBuilder ? () => setOpenLabId(null) : undefined,
      },
    ];

    if (!showBuilder) return trail;

    const labLabel = openLab?.name || (selected.kind === 'lab' ? selected.label : 'Lab');

    trail.push({
      key: 'lab',
      label: labLabel || 'Lab',
      onSelect:
        selected.kind === 'lab' ? undefined : () => setSelected({ kind: 'lab', label: labLabel }),
    });

    if (selected.kind === 'publication' || selected.kind === 'scenario') {
      trail.push({ key: selected.kind, label: selected.label });

      return trail;
    }

    if (selected.kind === 'item' || selected.kind === 'device') {
      const sectionName = scenarioById.get(selected.scenarioId)?.name || 'Section';
      const link = (links || []).find(
        (entry) => entry.lab === openLabId && entry.scenario === selected.scenarioId,
      );
      const scenarioId = selected.scenarioId;

      trail.push({
        key: 'scenario',
        label: sectionName,
        onSelect: () =>
          setSelected({
            kind: 'scenario',
            scenarioId,
            linkId: link?.id || 0,
            label: sectionName,
          }),
      });
      trail.push({ key: 'leaf', label: selected.label });
    }

    return trail;
  }, [current.label, showBuilder, openLab, openLabId, selected, scenarioById, links]);

  const newMenu = (
    <HbrDropdown>
      <HbrButton slot="trigger" caret variant="fill" size="small">
        <HbrIcon asset={plus} slot="prefix" />
        New
      </HbrButton>

      <HbrMenu>
        <HbrMenuItem value="lab" onClick={() => setLabEditor({ open: true, lab: null })}>
          <HbrIcon asset={stack} slot="prefix" />
          Lab
        </HbrMenuItem>

        <HbrMenuItem
          value="scenario"
          onClick={() => setScenarioEditor({ open: true, scenario: null })}
        >
          <HbrIcon asset={folder} slot="prefix" />
          Scenario
        </HbrMenuItem>

        <HbrMenuItem value="item" onClick={() => setItemEditor({ open: true, item: null })}>
          <HbrIcon asset={listBullets} slot="prefix" />
          Configuration item
        </HbrMenuItem>

        {/* The open section is a pre-fill, not a requirement: with a tree open this creates into the
            section being looked at, and from anywhere else the form asks which section to use. */}
        <HbrMenuItem
          value="device"
          onClick={() => setDeviceEditor({ scenario: contextScenario, device: null })}
        >
          <HbrIcon asset={network} slot="prefix" />
          Device
          {!!contextScenario && (
            <span slot="suffix" className="text-xs text-gray-500">
              in {contextScenario.name}
            </span>
          )}
        </HbrMenuItem>
      </HbrMenu>
    </HbrDropdown>
  );

  const nav = (
    <nav className="flex w-56 shrink-0 flex-col gap-4 border-r border-gray-15 pr-3">
      {[false, true].map((reference) => {
        const entries = visible.filter((entry) => !!entry.reference === reference);

        if (!entries.length) return null;

        return (
          <div key={reference ? 'reference' : 'objects'}>
            <p className="m-0 mb-1 px-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {reference ? 'Reference' : 'Library'}
            </p>

            <ul className="m-0 flex list-none flex-col gap-0.5 p-0">
              {entries.map((entry) => {
                const active = entry.key === current.key;
                const count = matches ? matches[entry.key] : undefined;

                return (
                  <li key={entry.key}>
                    <button
                      type="button"
                      onClick={() => {
                        setLibraryFocus(null);
                        go(entry.key);
                      }}
                      className={`flex w-full cursor-pointer items-start gap-2 rounded border-0 px-2 py-1.5 text-left ${
                        active ? 'bg-blue-100 font-semibold' : 'bg-transparent hover:bg-gray-100'
                      }`}
                    >
                      <HbrIcon asset={entry.asset} className="mt-0.5 text-base opacity-70" />

                      <span className="flex-1">
                        <span className="block text-sm">{entry.label}</span>
                        <span className="block text-xs text-gray-500">{entry.blurb}</span>
                      </span>

                      {/* Only where the term means something. The two reference tables are not
                          searched by this box, so a zero against them would be a false negative. */}
                      {count !== undefined && (
                        <span
                          className={`mt-0.5 rounded px-1.5 text-xs ${
                            count ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-500'
                          }`}
                          title={`${count} match${count === 1 ? '' : 'es'} for "${term.trim()}"`}
                        >
                          {count}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </nav>
  );

  /* ----------------------------------------------------------------------- *
   * The pane
   * ----------------------------------------------------------------------- */

  const pane = () => {
    if (showBuilder) {
      return (
        <LabBuilder
          labId={openLabId as number}
          canEdit={canEdit}
          selected={selected}
          onSelect={setSelected}
          onOpenInLibrary={(kind, id) => {
            // The lab stays open behind this, so returning to Labs comes back to the same tree at
            // the same depth rather than to the list.
            if (kind === 'scenario') {
              go('scenarios');
            } else if (kind === 'item') {
              go('items');
            } else {
              go('devices');
            }

            // The term is cleared, not kept: it narrowed the previous list and would hide the row
            // this jump exists to show.
            setTerm('');
            setLibraryFocus(id);
          }}
        />
      );
    }

    if (current.key === 'labs') {
      return (
        <LabsLibraryView
          canEdit={canEdit}
          searchTerm={term}
          onSearchTerm={setTerm}
          openLab={openLabInBuilder}
          onNew={() => setLabEditor({ open: true, lab: null })}
          onEdit={(lab) => setLabEditor({ open: true, lab })}
        />
      );
    }

    if (current.key === 'scenarios') {
      return (
        <ScenariosLibraryView
          canEdit={canEdit}
          searchTerm={term}
          onSearchTerm={setTerm}
          selectedId={libraryFocus}
          openScenario={openScenarioInBuilder}
          openLab={openLabInBuilder}
          onNew={() => setScenarioEditor({ open: true, scenario: null })}
          onEdit={(scenario) => setScenarioEditor({ open: true, scenario })}
        />
      );
    }

    if (current.key === 'items') {
      return (
        <ConfigurationItemsLibraryView
          canEdit={canEdit}
          searchTerm={term}
          onSearchTerm={setTerm}
          selectedId={libraryFocus}
          // Opening an item *is* editing it: the catalogue row is all there is, and which of its
          // attachments you meant is a question the builder answers, not this list.
          openItem={(itemId) =>
            setItemEditor({
              open: true,
              item: (items || []).find((entry) => entry.id === itemId) || null,
            })
          }
          openScenario={openScenarioInBuilder}
          onNew={() => setItemEditor({ open: true, item: null })}
        />
      );
    }

    if (current.key === 'devices') {
      return (
        <DevicesLibraryView
          canEdit={canEdit}
          searchTerm={term}
          onSearchTerm={setTerm}
          selectedId={libraryFocus}
          openDevice={openDeviceEditor}
          openScenario={openScenarioInBuilder}
          // Opened with no section pre-ticked: this is the library, and a box authored here is
          // attached to whichever sections the form's list is given, including none.
          onNew={() => setDeviceEditor({ scenario: null, device: null })}
        />
      );
    }

    if (current.key === 'deviceTypes') return <LabDeviceTypesPanel canEdit={canEdit} />;

    return <LabExecutionMethodsPanel canEdit={canEdit} />;
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <NavHistoryTracker title={current.label} />

      {/* "Lab Generation" rather than the spec's "Lab Administration", because that is what the
          admin menu entry this page is reached by says, and two names for one page is worse than
          the less accurate one. */}
      <div className="flex flex-wrap items-center gap-3 border-b border-gray-15 pb-4">
        <h1 className="m-0 mr-auto text-gray-60 font-semibold leading-5">Lab Generation</h1>

        <LabSearchBox
          value={term}
          onChange={setTerm}
          placeholder="Search labs, scenarios, items and devices"
          className="min-w-[280px]"
        />

        {canEdit && newMenu}
      </div>

      {/* The trail is the only thing on the page that says how deep the selection is, so it is
          drawn even when it is one crumb long - a breadcrumb that appears and disappears is one
          more thing to notice. */}
      <div className="py-3">
        <HbrBreadcrumb>
          {crumbs.map((crumb) => (
            <HbrBreadcrumbItem key={crumb.key}>
              {crumb.onSelect ? (
                <button
                  type="button"
                  onClick={crumb.onSelect}
                  className="cursor-pointer border-0 bg-transparent p-0 text-inherit underline-offset-2 hover:underline"
                >
                  {crumb.label}
                </button>
              ) : (
                <span>{crumb.label}</span>
              )}
            </HbrBreadcrumbItem>
          ))}
        </HbrBreadcrumb>
      </div>

      <div className="flex flex-1 gap-4" style={{ minHeight: 0, minWidth: 0 }}>
        {nav}

        <div className="flex-1" style={{ minHeight: 0, minWidth: 0 }}>
          {pane()}
        </div>
      </div>

      {/* Hosted here, once, so a save reported from six levels down still reaches the screen. */}
      <LabToastHost />

      <LabEditorModal
        open={labEditor.open}
        lab={labEditor.lab}
        existing={labs || []}
        onClose={() => setLabEditor({ open: false, lab: null })}
        // A new lab opens in the builder, because an empty lab is the one thing nobody wants to
        // stop at. An edited one does not: the admin was in a list, and being moved into a tree
        // after renaming something is a jump they did not ask for.
        onSaved={(saved) => {
          if (!labEditor.lab) openLabInBuilder(saved.id);
        }}
      />

      <LabScenarioEditorModal
        open={scenarioEditor.open}
        scenario={scenarioEditor.scenario}
        onClose={() => setScenarioEditor({ open: false, scenario: null })}
      />

      <LabConfigurationItemEditorModal
        open={itemEditor.open}
        item={itemEditor.item}
        onClose={() => setItemEditor({ open: false, item: null })}
      />

      {!!deviceEditor && (
        <LabDeviceEditorModal
          open
          scenario={deviceEditor.scenario}
          device={deviceEditor.device}
          onClose={() => setDeviceEditor(null)}
        />
      )}
    </div>
  );
};

export default AdminLabGeneration;
